import React, { useState, useRef, useEffect, useCallback } from "react";
import { App, Modal } from "antd";
import {
  UploadOutlined, FilePdfOutlined, EditOutlined,
  DownloadOutlined, DeleteOutlined, CheckCircleFilled,
  UndoOutlined, ClearOutlined, CheckOutlined,
  PictureOutlined, FontSizeOutlined, CalendarOutlined,
  UserOutlined, SafetyCertificateOutlined,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import { downloadAuthenticatedFile } from "../../utils/downloadFile";
import "../PdfTools.css";
import "./ESignPdf.css";

const countPdfPages = async (pdfFile) => {
  const buffer = await pdfFile.arrayBuffer();
  const pdfText = new TextDecoder("latin1").decode(buffer);
  const pageMatches = pdfText.match(/\/Type\s*\/Page\b(?!s)/g);

  if (pageMatches?.length) {
    return pageMatches.length;
  }

  const countMatches = [...pdfText.matchAll(/\/Count\s+(\d+)/g)]
    .map((match) => Number(match[1]))
    .filter((count) => Number.isFinite(count) && count > 0);

  return countMatches.length ? Math.max(...countMatches) : 0;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const dataUrlToFile = async (dataUrl, fileName = "signature.png") => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || "image/png" });
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const createSignItem = ({ type, pageIndex, dataUrl = "", file = null, value = "" }) => ({
  id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type,
  pageIndex,
  dataUrl,
  file,
  value,
  xPercent: 62,
  yPercent: type === "companyStamp" ? 66 : 76,
  widthPercent: type === "companyStamp" ? 18 : 24,
  heightPercent: type === "companyStamp" ? 12 : 7,
});

// ── Signature Canvas ──────────────────────────────────────────────
const SignatureCanvas = ({ onDone, onCancel }) => {
  const canvasRef   = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [strokes,  setStrokes]  = useState([]); // array of arrays of {x,y}
  const [color,    setColor]    = useState("#000000");

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const redraw = useCallback((stks, col) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = col || color;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    stks.forEach((stroke) => {
      if (stroke.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      stroke.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    });
  }, [color]);

  const startStroke = (e) => {
    e.preventDefault();
    const pos = getPos(e, canvasRef.current);
    setStrokes((prev) => [...prev, [pos]]);
    setDrawing(true);
  };

  const addPoint = (e) => {
    e.preventDefault();
    if (!drawing) return;
    const pos = getPos(e, canvasRef.current);
    setStrokes((prev) => {
      const next = [...prev];
      next[next.length - 1] = [...next[next.length - 1], pos];
      return next;
    });
  };

  const endStroke = () => setDrawing(false);

  const undo = () => {
    setStrokes((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -1);
    });
  };

  const clear = () => { setStrokes([]); };

  useEffect(() => { redraw(strokes, color); }, [strokes, color, redraw]);

  const handleDone = () => {
    if (strokes.length === 0) { onCancel(); return; }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    onDone(dataUrl);
  };

  const COLORS = ["#000000", "#e5322d", "#1a3f9e"];

  return (
    <div className="sig-wrap">
      <canvas
        ref={canvasRef}
        width={580}
        height={260}
        className="sig-canvas"
        onMouseDown={startStroke}
        onMouseMove={addPoint}
        onMouseUp={endStroke}
        onMouseLeave={endStroke}
        onTouchStart={startStroke}
        onTouchMove={addPoint}
        onTouchEnd={endStroke}
      />
      <div className="sig-toolbar">
        <div className="sig-colors">
          {COLORS.map((c) => (
            <button key={c} className={`sig-color-btn ${color === c ? "active" : ""}`}
              style={{ background: c }} onClick={() => setColor(c)} />
          ))}
        </div>
        <div className="sig-actions">
          <button className="sig-tool-btn" onClick={undo} title="Undo"><UndoOutlined /></button>
          <button className="sig-tool-btn" onClick={clear} title="Clear"><ClearOutlined /></button>
          <button className="sig-done-btn" onClick={handleDone}><CheckOutlined /> Done</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const ESignPdf = () => {
  const { message } = App.useApp();
  const [file, setFile] = useState(null);
  const [readingPages, setReadingPages] = useState(false);
  const [pageReadFailed, setPageReadFailed] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [activePage, setActivePage] = useState(0);
  const [signItems, setSignItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [dragState, setDragState] = useState(null);
  const [showSigModal, setShowSigModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [signedFileName, setSignedFileName] = useState("");
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const fileRef = useRef(null);
  const signatureUploadRef = useRef(null);
  const digitalUploadRef = useRef(null);
  const stampUploadRef = useRef(null);
  const pageRefs = useRef({});

  const isModule = window.location.pathname.includes("/dashboard/");
  const pages = pageCount > 0 ? Array.from({ length: pageCount }, (_, i) => i) : [];
  const selectedItem = signItems.find((item) => item.id === selectedItemId);

  const resetSigningState = () => {
    setDownloadUrl("");
    setSignedFileName("");
    setDownloading(false);
    setSignItems([]);
    setSelectedItemId("");
    setActivePage(0);
  };

  const pickFile = async (incoming) => {
    const f = Array.from(incoming).find((item) =>
      item.type === "application/pdf" || item.name.endsWith(".pdf")
    );
    if (!f) { message.error("Please select a PDF file."); return; }

    setPdfPreviewUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return URL.createObjectURL(f);
    });
    setFile(f);
    setPageCount(0);
    setPageReadFailed(false);
    setReadingPages(true);
    resetSigningState();

    try {
      const detectedPageCount = await countPdfPages(f);
      if (detectedPageCount > 0) {
        setPageCount(detectedPageCount);
      } else {
        setPageReadFailed(true);
        message.warning("Unable to read page count automatically. Please enter it manually.");
      }
    } catch {
      setPageReadFailed(true);
      message.warning("Unable to read page count automatically. Please enter it manually.");
    } finally {
      setReadingPages(false);
    }
  };

  const reset = () => {
    setPdfPreviewUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return "";
    });
    setFile(null);
    setPageCount(0);
    setPageReadFailed(false);
    setReadingPages(false);
    resetSigningState();
  };

  useEffect(() => () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
  }, [pdfPreviewUrl]);

  const addItem = (item) => {
    const nextItem = createSignItem({ pageIndex: activePage, ...item });
    setSignItems((prev) => [...prev, nextItem]);
    setSelectedItemId(nextItem.id);
  };

  const addTextItem = (type) => {
    const defaults = {
      text: "Text",
      name: "Name",
      date: new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-"),
    };
    const label = type === "name" ? "Name" : type === "date" ? "Date" : "Text";
    const value = window.prompt(`Enter ${label}`, defaults[type]);
    if (!value) return;
    addItem({ type, value });
  };

  const handleSignatureDone = (dataUrl) => {
    addItem({ type: "signature", dataUrl });
    setShowSigModal(false);
  };

  const handleImageUpload = async (event, type) => {
    const uploadedFile = event.target.files?.[0];
    event.target.value = "";
    if (!uploadedFile) return;
    if (!uploadedFile.type.startsWith("image/")) {
      message.error("Please select a valid image file.");
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(uploadedFile);
      addItem({ type, dataUrl, file: uploadedFile });
    } catch {
      message.error("Unable to read selected image.");
    }
  };

  const updateItem = (id, patch) => {
    setSignItems((prev) =>
      prev.map((item) => item.id === id ? { ...item, ...patch } : item)
    );
  };

  const removeItem = (id) => {
    setSignItems((prev) => prev.filter((item) => item.id !== id));
    if (selectedItemId === id) {
      setSelectedItemId("");
    }
  };

  const focusPage = (pageIndex) => {
    setActivePage(pageIndex);
    setSelectedItemId("");
  };

  const onItemMouseDown = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    const pageEl = pageRefs.current[item.pageIndex];
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();
    setSelectedItemId(item.id);
    setActivePage(item.pageIndex);
    setDragState({
      id: item.id,
      pageIndex: item.pageIndex,
      offsetXPercent: ((event.clientX - rect.left) / rect.width) * 100 - item.xPercent,
      offsetYPercent: ((event.clientY - rect.top) / rect.height) * 100 - item.yPercent,
    });
  };

  useEffect(() => {
    const move = (event) => {
      if (!dragState) return;
      const pageEl = pageRefs.current[dragState.pageIndex];
      if (!pageEl) return;
      const rect = pageEl.getBoundingClientRect();
      const item = signItems.find((entry) => entry.id === dragState.id);
      if (!item) return;
      const x = ((event.clientX - rect.left) / rect.width) * 100 - dragState.offsetXPercent;
      const y = ((event.clientY - rect.top) / rect.height) * 100 - dragState.offsetYPercent;
      updateItem(item.id, {
        xPercent: clamp(x, 0, 100 - item.widthPercent),
        yPercent: clamp(y, 0, 100 - item.heightPercent),
      });
    };
    const stop = () => setDragState(null);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
    };
  }, [dragState, signItems]);

  const buildSigningPayload = async (formData) => {
    const items = [];
    let firstImageFile = null;
    let firstImageItem = null;

    for (const [index, item] of signItems.entries()) {
      const isImageItem = Boolean(item.dataUrl);
      const fileKey = isImageItem ? `${item.type}_${index}` : "";
      let uploadFile = item.file;

      if (isImageItem && !uploadFile) {
        uploadFile = await dataUrlToFile(item.dataUrl, `${fileKey}.png`);
      }

      if (uploadFile) {
        formData.append(fileKey, uploadFile);
        if (!firstImageFile) {
          firstImageFile = uploadFile;
          firstImageItem = item;
        }
      }

      items.push({
        id: item.id,
        type: item.type,
        pageNumber: item.pageIndex + 1,
        xPercent: Number(item.xPercent.toFixed(2)),
        yPercent: Number(item.yPercent.toFixed(2)),
        widthPercent: Number(item.widthPercent.toFixed(2)),
        heightPercent: Number(item.heightPercent.toFixed(2)),
        fileKey,
        value: item.value || "",
      });
    }

    formData.append("items", JSON.stringify(items));

    // Legacy fields keep the current backend from breaking while the new items payload is added.
    if (firstImageFile && firstImageItem) {
      formData.append("signatureFile", firstImageFile);
      formData.append("pageNumber", (firstImageItem.pageIndex + 1).toString());
      formData.append("positionX", Math.round(firstImageItem.xPercent).toString());
      formData.append("positionY", Math.round(firstImageItem.yPercent).toString());
      formData.append("scale", (firstImageItem.widthPercent / 24).toFixed(2));
    }
  };

  const handleSave = async () => {
    if (!file) return;
    if (signItems.length === 0) {
      message.warning("Please add at least one signature, image, stamp, or text field.");
      return;
    }

    setUploading(true);
    setDownloadUrl("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("Source", isModule ? "Private-Web" : "Public-Web");
      await buildSigningPayload(formData);

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/esign-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!res.data.error && res.data.data?.downloadUrl) {
        setDownloadUrl(res.data.data.downloadUrl);
        setSignedFileName(res.data.data.fileName || "signed.pdf");
        message.success("PDF signed successfully!");
      } else {
        message.error(res.data.message || "E-sign failed.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  const downloadSignedPdf = async () => {
    if (!downloadUrl || downloading) return;
    setDownloading(true);

    try {
      await downloadAuthenticatedFile(downloadUrl, {
        token: localStorage.getItem("token") ?? "",
        fileName: signedFileName || "signed.pdf",
      });
    } catch {
      message.error("Unable to download signed PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const itemLabel = (item) => ({
    signature: "Signature",
    digitalSignature: "Digital Sign",
    companyStamp: "Company Stamp",
    name: "Name",
    date: "Date",
    text: "Text",
  }[item.type] || "Field");

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`pt-root ${isModule ? "pt-module" : ""}`}>
        <div className="pt-inner pt-wide">
          <div className="pt-header">
            <div className="pt-header-icon" style={{ background: "#fff7ed" }}>
              <EditOutlined style={{ color: "#c84b1e" }} />
            </div>
            <div>
              <h1 className="pt-title">E-sign PDF</h1>
              <p className="pt-subtitle">
                Add signatures, digital-sign images, stamps, names, dates, and text on any PDF page.
              </p>
            </div>
          </div>

          {!file && !downloadUrl && (
            <div
              className={`pt-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
                onChange={(e) => { pickFile(e.target.files); e.target.value = ""; }} />
              <FilePdfOutlined className="pt-dropzone-icon" />
              <div className="pt-dropzone-text">Click to select or drag your PDF here</div>
              <div className="pt-dropzone-hint">Only PDF files supported</div>
              <button className="pt-choose-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                <UploadOutlined /> Choose File
              </button>
            </div>
          )}

          {file && !downloadUrl && (
            <>
              <div className="pt-file-row">
                <FilePdfOutlined className="pt-file-icon" />
                <span className="pt-file-name">{file.name}</span>
                <span className="pt-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                <button className="pt-file-remove" onClick={reset}><DeleteOutlined /></button>
              </div>

              {(readingPages || pageReadFailed) && (
                <div className="es-page-read-card">
                  {readingPages ? (
                    <span className="es-page-status"><span className="pt-spinner" /> Reading PDF page count...</span>
                  ) : (
                    <div className="es-step-row">
                      <span className="es-manual-label">How many pages does this PDF have?</span>
                      <input type="number" min="1" max="500" className="es-num-input" placeholder="e.g. 5" id="es-page-count" />
                      <button className="pt-choose-btn" style={{ marginTop: 0 }}
                        onClick={() => {
                          const v = parseInt(document.getElementById("es-page-count").value);
                          if (v > 0) {
                            setPageCount(v);
                            setPageReadFailed(false);
                          }
                        }}>
                        Confirm
                      </button>
                    </div>
                  )}
                </div>
              )}

              {pageCount > 0 && (
                <div className="es-workbench">
                  <aside className="es-thumbnails">
                    <div className="es-panel-title">Pages</div>
                    {pages.map((pageIndex) => (
                      <button
                        key={pageIndex}
                        className={`es-thumb ${activePage === pageIndex ? "active" : ""}`}
                        onClick={() => focusPage(pageIndex)}
                        type="button"
                      >
                        <div className="es-thumb-page">
                          <FilePdfOutlined />
                          <span>{signItems.filter((item) => item.pageIndex === pageIndex).length}</span>
                        </div>
                        <strong>{pageIndex + 1}</strong>
                      </button>
                    ))}
                  </aside>

                  <main className="es-viewer">
                    <div className="es-viewer-toolbar">
                      <button type="button" onClick={() => focusPage(Math.max(0, activePage - 1))} disabled={activePage === 0}>‹</button>
                      <span>Page {activePage + 1} / {pageCount}</span>
                      <button type="button" onClick={() => focusPage(Math.min(pageCount - 1, activePage + 1))} disabled={activePage === pageCount - 1}>›</button>
                    </div>
                    <section
                      key={activePage}
                      className="es-pdf-page active"
                      ref={(node) => { pageRefs.current[activePage] = node; }}
                      onClick={() => setSelectedItemId("")}
                    >
                      {pdfPreviewUrl ? (
                        <iframe
                          className="es-pdf-frame"
                          title={`PDF page ${activePage + 1}`}
                          src={`${pdfPreviewUrl}#page=${activePage + 1}&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                        />
                      ) : (
                        <div className="es-page-watermark">PDF Preview</div>
                      )}
                      <div className="es-overlay-layer">
                        {signItems.filter((item) => item.pageIndex === activePage).map((item) => (
                            <div
                              key={item.id}
                              className={`es-placed-item ${selectedItemId === item.id ? "selected" : ""} ${item.dataUrl ? "image" : "text"}`}
                              style={{
                                left: `${item.xPercent}%`,
                                top: `${item.yPercent}%`,
                                width: `${item.widthPercent}%`,
                                height: `${item.heightPercent}%`,
                              }}
                              onMouseDown={(event) => onItemMouseDown(event, item)}
                            >
                              {item.dataUrl ? (
                                <img src={item.dataUrl} alt={itemLabel(item)} draggable={false} />
                              ) : (
                                <span>{item.value}</span>
                              )}
                            </div>
                          ))}
                      </div>
                      </section>
                  </main>

                  <aside className="es-options">
                    <div className="es-options-head">
                      <h2>Signing options</h2>
                      <span>{signItems.length} field{signItems.length !== 1 ? "s" : ""}</span>
                    </div>

                    <div className="es-panel-title">Type</div>
                    <div className="es-type-grid">
                      <button type="button" className="es-type-card" onClick={() => setShowSigModal(true)}>
                        <EditOutlined />
                        <span>Simple Signature</span>
                      </button>
                      <button type="button" className="es-type-card" onClick={() => digitalUploadRef.current?.click()}>
                        <PictureOutlined />
                        <span>Digital Signature</span>
                      </button>
                    </div>

                    <input ref={signatureUploadRef} type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, "signature")} />
                    <input ref={digitalUploadRef} type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, "digitalSignature")} />
                    <input ref={stampUploadRef} type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, "companyStamp")} />

                    <div className="es-panel-title">Add fields</div>
                    <div className="es-field-list">
                      <button type="button" onClick={() => signatureUploadRef.current?.click()}><PictureOutlined /> Upload Signature Image</button>
                      <button type="button" onClick={() => addTextItem("name")}><UserOutlined /> Name</button>
                      <button type="button" onClick={() => addTextItem("date")}><CalendarOutlined /> Date</button>
                      <button type="button" onClick={() => addTextItem("text")}><FontSizeOutlined /> Text</button>
                      <button type="button" onClick={() => stampUploadRef.current?.click()}><SafetyCertificateOutlined /> Company Stamp</button>
                    </div>

                    {selectedItem && (
                      <div className="es-field-editor">
                        <div className="es-panel-title">Selected field</div>
                        <div className="es-editor-name">{itemLabel(selectedItem)} · Page {selectedItem.pageIndex + 1}</div>
                        <label>
                          Width
                          <input
                            type="range"
                            min="8"
                            max="60"
                            value={selectedItem.widthPercent}
                            onChange={(e) => updateItem(selectedItem.id, { widthPercent: Number(e.target.value) })}
                          />
                        </label>
                        <label>
                          Height
                          <input
                            type="range"
                            min="4"
                            max="30"
                            value={selectedItem.heightPercent}
                            onChange={(e) => updateItem(selectedItem.id, { heightPercent: Number(e.target.value) })}
                          />
                        </label>
                        {!selectedItem.dataUrl && (
                          <input
                            className="es-editor-text"
                            value={selectedItem.value}
                            onChange={(e) => updateItem(selectedItem.id, { value: e.target.value })}
                          />
                        )}
                        <button type="button" className="es-remove-field" onClick={() => removeItem(selectedItem.id)}>
                          <DeleteOutlined /> Remove field
                        </button>
                      </div>
                    )}

                    <button className="es-sign-btn" onClick={handleSave} disabled={uploading || signItems.length === 0}>
                      {uploading ? <span className="pt-spinner" /> : <CheckOutlined />}
                      {uploading ? "Signing..." : "Sign PDF"}
                    </button>
                  </aside>
                </div>
              )}
            </>
          )}

          {downloadUrl && (
            <div className="pt-success">
              <CheckCircleFilled className="pt-success-icon" />
              <div className="pt-success-title">PDF Signed!</div>
              <p className="pt-success-sub">Your fields have been applied to the PDF.</p>
              <div className="pt-success-actions">
                <button type="button" className="pt-download-btn" onClick={downloadSignedPdf} disabled={downloading}>
                  {downloading ? <span className="pt-spinner" /> : <DownloadOutlined />}
                  {downloading ? "Downloading..." : "Download Signed PDF"}
                </button>
                <button className="pt-restart-btn" onClick={reset}>Sign another</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={showSigModal}
        onCancel={() => setShowSigModal(false)}
        footer={null}
        title={<span style={{ fontFamily: "Poppins", fontWeight: 700 }}>Draw Your Signature</span>}
        width={640}
        centered
        destroyOnClose
      >
        <SignatureCanvas
          onDone={handleSignatureDone}
          onCancel={() => setShowSigModal(false)}
        />
      </Modal>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default ESignPdf;
